/**
 * FunnelEconomicsWaterfall (MA-BACKLOG-051)
 * 
 * Vertical waterfall visualization showing the complete conversion funnel:
 * Visitors → Leads → Appointments → Clients → Profit
 * 
 * Displays:
 * - Absolute count/value at each stage
 * - Conversion rate between consecutive stages
 * - Visual representation of funnel progression
 * 
 * Data source: /api/war-room/funnel-economics-waterfall
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Users, 
  UserPlus, 
  Calendar, 
  UserCheck, 
  DollarSign,
  ChevronDown,
  ArrowDown,
  TrendingDown,
  Activity,
  AlertTriangle,
  GitBranch
} from 'lucide-react';

// Format large numbers with K/M suffix
const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Format currency
const formatCurrency = (value, compact = true) => {
  if (value === null || value === undefined) return '—';
  
  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

// Stage configuration
const STAGES = [
  {
    key: 'visitors',
    label: 'Visitors',
    description: 'Total funnel sessions',
    icon: Users,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30'
  },
  {
    key: 'leads',
    label: 'Leads',
    description: 'Captured leads',
    icon: UserPlus,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    conversionKey: 'visitor_to_lead_rate'
  },
  {
    key: 'appointments',
    label: 'Appointments',
    description: 'Booked appointments',
    icon: Calendar,
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-500/20',
    borderColor: 'border-indigo-500/30',
    conversionKey: 'lead_to_appointment_rate'
  },
  {
    key: 'clients',
    label: 'Clients',
    description: 'Converted clients',
    icon: UserCheck,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/30',
    conversionKey: 'appointment_to_client_rate'
  },
  {
    key: 'profit',
    label: 'Profit',
    description: 'Total profit generated',
    icon: DollarSign,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    isCurrency: true
  }
];

/**
 * Single stage in the waterfall
 */
function WaterfallStage({ stage, value, maxValue, conversionRate, isLast }) {
  const Icon = stage.icon;
  const isProfit = stage.key === 'profit';
  
  // Calculate bar width relative to max (for visual comparison)
  // Profit stage doesn't use bar width comparison
  const barWidth = isProfit ? 100 : Math.max((value / (maxValue || 1)) * 100, 5);
  
  return (
    <div className="relative" data-testid={`waterfall-stage-${stage.key}`}>
      {/* Stage Content */}
      <div className={`relative rounded-xl border ${stage.borderColor} ${stage.bgColor} p-4 transition-all hover:border-opacity-60`}>
        <div className="flex items-center justify-between">
          {/* Left: Icon and Label */}
          <div className="flex items-center gap-3">
            <div className={`rounded-lg ${stage.bgColor} p-2.5`}>
              <Icon className={`h-5 w-5 ${stage.color}`} />
            </div>
            <div>
              <h4 className="font-semibold text-slate-100">{stage.label}</h4>
              <p className="text-xs text-slate-400">{stage.description}</p>
            </div>
          </div>
          
          {/* Right: Value */}
          <div className="text-right">
            <p className={`text-2xl font-bold ${stage.color}`}>
              {isProfit ? formatCurrency(value) : formatNumber(value)}
            </p>
            {conversionRate !== undefined && conversionRate !== null && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge 
                      className={`mt-1 text-xs ${stage.bgColor} ${stage.color} border ${stage.borderColor} cursor-help`}
                    >
                      {formatPercent(conversionRate)} conv
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-slate-900 border-slate-700 text-xs">
                    Conversion rate from previous stage
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Progress Bar (not shown for profit) */}
        {!isProfit && (
          <div className="mt-3 h-2 w-full rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${
                stage.key === 'visitors' ? 'bg-blue-500' :
                stage.key === 'leads' ? 'bg-cyan-500' :
                stage.key === 'appointments' ? 'bg-indigo-500' :
                'bg-purple-500'
              }`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        )}
      </div>
      
      {/* Connector Arrow (if not last stage) */}
      {!isLast && (
        <div className="flex flex-col items-center py-2">
          <div className="h-4 w-0.5 bg-gradient-to-b from-slate-500 to-slate-600" />
          <ChevronDown className="h-5 w-5 text-slate-500 -mt-1" />
        </div>
      )}
    </div>
  );
}

/**
 * Summary Stats at the bottom of waterfall
 */
function WaterfallSummary({ data }) {
  const overallConversion = data.visitors > 0 
    ? (data.clients / data.visitors) 
    : 0;
  
  const profitPerVisitor = data.visitors > 0 
    ? (data.profit / data.visitors) 
    : 0;
  
  const profitPerLead = data.leads > 0 
    ? (data.profit / data.leads) 
    : 0;
  
  return (
    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
      <div className="text-center">
        <p className="text-xs text-slate-400 uppercase tracking-wide">Overall Conv</p>
        <p className="text-lg font-bold text-slate-100">{formatPercent(overallConversion)}</p>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-400 uppercase tracking-wide">$/Visitor</p>
        <p className="text-lg font-bold text-emerald-400">{formatCurrency(profitPerVisitor, false)}</p>
      </div>
      <div className="text-center">
        <p className="text-xs text-slate-400 uppercase tracking-wide">$/Lead</p>
        <p className="text-lg font-bold text-emerald-400">{formatCurrency(profitPerLead, false)}</p>
      </div>
    </div>
  );
}

/**
 * Main Waterfall Component
 */
export function FunnelEconomicsWaterfall({ data, loading = false, error = null, className = '' }) {
  // Loading state
  if (loading) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="waterfall-loading">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-48">
            <Activity className="h-6 w-6 text-slate-400 animate-pulse" />
            <span className="ml-2 text-slate-400">Loading funnel data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Error state
  // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
  if (error) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="waterfall-error">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center">
            <GitBranch className="h-10 w-10 text-slate-600 mb-3" />
            <span className="text-slate-300 font-medium">Funnel Economics Unavailable</span>
            <span className="text-slate-500 text-sm mt-1">Unable to load funnel waterfall data at this time.</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // No data state
  if (!data) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="waterfall-empty">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <TrendingDown className="h-8 w-8 mb-2" />
            <p>No funnel data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Find max count for scaling bars (excluding profit)
  const maxCount = Math.max(
    data.visitors || 0,
    data.leads || 0,
    data.appointments || 0,
    data.clients || 0,
    1
  );
  
  return (
    <Card className={`glass border-white/10 ${className}`} data-testid="funnel-economics-waterfall">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <ArrowDown className="h-5 w-5 text-indigo-400" />
          Funnel Economics
          <Badge className="ml-2 text-xs bg-white/5 text-slate-400 border-white/10">
            Waterfall
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-0">
        {/* Waterfall Stages */}
        <div className="space-y-0">
          {STAGES.map((stage, idx) => (
            <WaterfallStage
              key={stage.key}
              stage={stage}
              value={data[stage.key]}
              maxValue={maxCount}
              conversionRate={stage.conversionKey ? data[stage.conversionKey] : undefined}
              isLast={idx === STAGES.length - 1}
            />
          ))}
        </div>
        
        {/* Summary Stats */}
        <WaterfallSummary data={data} />
      </CardContent>
    </Card>
  );
}

export default FunnelEconomicsWaterfall;
